import React from 'react';

import useNotifications from '../../../_lib/notifications/useNotifications';
import {
  QR_EXPORT_PIXELS,
  QrCodePng,
  serializeSvg,
  svgMarkupToPng,
  writeImageToClipboard,
} from './qrCodeImage.utils';

export interface QrCodeImage {
  /** Attach to the element wrapping the `<svg>` that gets rasterised. */
  readonly svgHostRef: React.RefObject<HTMLDivElement>;
  /** Null until the PNG is ready, or if rasterising failed. */
  readonly pngDataUrl: string | null;
  readonly copyQrCode: () => Promise<void>;
  readonly isCopying: boolean;
}

const messageFor = (error: unknown): string | undefined =>
  error instanceof Error ? error.message : undefined;

/**
 * Rasterises the rendered QR SVG to a PNG once its payload arrives, and copies
 * that PNG on demand.
 *
 * Failing to rasterise is deliberately quiet: `pngDataUrl` stays null, the
 * caller keeps showing the SVG, and the user loses the copy button but not the
 * ability to finish enabling MFA.
 */
export const useQrCodeImage = (qrValue: string | null): QrCodeImage => {
  const notifications = useNotifications();

  const svgHostRef = React.useRef<HTMLDivElement>(null);
  const [png, setPng] = React.useState<QrCodePng | null>(null);
  const [isCopying, setIsCopying] = React.useState(false);

  React.useEffect(() => {
    const svg = svgHostRef.current?.querySelector('svg');

    if (qrValue == null || svg == null) {
      setPng(null);
      return undefined;
    }

    let cancelled = false;

    svgMarkupToPng(serializeSvg(svg), QR_EXPORT_PIXELS)
      .then((result) => {
        if (!cancelled) {
          setPng(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPng(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [qrValue]);

  const copyQrCode = React.useCallback(async () => {
    if (png == null) {
      return;
    }

    setIsCopying(true);

    try {
      await writeImageToClipboard(png.blob);
      notifications.success('QR code copied to clipboard');
    } catch (error) {
      notifications.error('Could not copy the QR code', messageFor(error));
    } finally {
      setIsCopying(false);
    }
  }, [notifications, png]);

  return {
    svgHostRef,
    pngDataUrl: png?.dataUrl ?? null,
    copyQrCode,
    isCopying,
  };
};
